import type { WasteLineStats } from "@/hooks/use-line-stats";

const DAY_MS = 86_400_000;

/**
 * The line-stats window for the dashboard's date range. The current period is
 * [from, to] measured in whole days (the endpoint takes an integer day length);
 * `n_periods = 2` then yields the equivalent previous period as `periods[0]`.
 * An open-ended `to` (relative presets run up to now) is closed at `now`.
 * Returns null for the all-time range — there is no bounded period to compare.
 */
export function lineStatsWindow(
  dateRange: { from: string | null; to: string | null },
  now: Date,
): { periodStart: string; periodLengthDays: number } | null {
  if (!dateRange.from) return null;
  const from = new Date(dateRange.from).getTime();
  const to = dateRange.to ? new Date(dateRange.to).getTime() : now.getTime();
  const periodLengthDays = Math.max(1, Math.round((to - from) / DAY_MS));
  return { periodStart: dateRange.from, periodLengthDays };
}

export type PulseCell = {
  lineKey: string;
  label: string;
  /** Image-backed captures in the current period. */
  captures: number;
  /** Image-backed captures in the previous period. */
  prevCaptures: number;
};

export type PulsePair = {
  needsChanging: PulseCell | null;
  working: PulseCell | null;
};

/** Captures in the current (latest) period — the last bucket is chronological. */
function currentCaptures(line: WasteLineStats): number {
  return line.periods[line.periods.length - 1]?.tagged_capture_count ?? 0;
}

/** Captures in the previous period — the second-to-last bucket. */
function prevCaptures(line: WasteLineStats): number {
  return line.periods[line.periods.length - 2]?.tagged_capture_count ?? 0;
}

function toCell(line: WasteLineStats): PulseCell {
  return {
    lineKey: line.line_key,
    label: line.label,
    captures: currentCaptures(line),
    prevCaptures: prevCaptures(line),
  };
}

/** The line to fix: the most-captured line this period. Null when nothing has
 * captures this period. */
export function selectNeedsChanging(lines: WasteLineStats[]): PulseCell | null {
  let best: WasteLineStats | null = null;
  for (const line of lines) {
    if (currentCaptures(line) <= 0) continue;
    if (!best || currentCaptures(line) > currentCaptures(best)) best = line;
  }
  return best ? toCell(best) : null;
}

/** The line that worked: primary = had captures last period, none this period
 * (pick the biggest such drop-to-zero); fallback = the largest period-on-period
 * capture drop. Excludes the needs-changing line so the pair never repeats. */
export function selectWorking(lines: WasteLineStats[], excludeKey: string | null): PulseCell | null {
  const eligible = lines.filter((line) => line.line_key !== excludeKey);

  let best: WasteLineStats | null = null;
  for (const line of eligible) {
    if (prevCaptures(line) > 0 && currentCaptures(line) === 0) {
      if (!best || prevCaptures(line) > prevCaptures(best)) best = line;
    }
  }
  if (best) return toCell(best);

  for (const line of eligible) {
    const drop = prevCaptures(line) - currentCaptures(line);
    if (drop <= 0) continue;
    if (!best || drop > prevCaptures(best) - currentCaptures(best)) best = line;
  }
  return best ? toCell(best) : null;
}

/**
 * The Pulse pair, or null to hide the strip entirely — the site has no captures
 * in either period, so there is nothing to show (an empty shell would be noise).
 */
export function selectPulse(lines: WasteLineStats[]): PulsePair | null {
  // Unidentified (L1) is never listed as a Pulse cell — the "on totals, never
  // listed" rule What's-working and The Proof already follow (POS2-924/936).
  // Filtering here drops it from both cells and the has-captures gate, so a site
  // whose only captures are unidentified shows no strip rather than an L1 cell.
  const named = lines.filter((line) => line.line_key !== "unidentified");
  const anyCaptures = named.some((line) => currentCaptures(line) > 0 || prevCaptures(line) > 0);
  if (!anyCaptures) return null;

  const needsChanging = selectNeedsChanging(named);
  const working = selectWorking(named, needsChanging?.lineKey ?? null);
  if (!needsChanging && !working) return null;

  return { needsChanging, working };
}
